import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AppProvider, useApp } from './context/AppContext';
import { LanguageProvider, useLanguage } from './i18n/LanguageContext';
import { ThemeProvider } from './context/ThemeContext';
import { logout } from './services/firebase';
import { handlePushDeepLink, initPassengerMessaging } from './services/notifications';
import { initAndroidBackBridge, registerAndroidBackHandler } from './utils/androidBack';
import { checkForAppUpdate } from './utils/appUpdate';
import { initGeoDeepLinking } from './utils/geoIntent';
import Splash from './pages/Splash';
import Login from './pages/Login';
import Book from './pages/Book';
import ActiveRide from './pages/ActiveRide';
import RideHistory from './pages/RideHistory';
import RideDetails from './pages/RideDetails';
import IntertownTripDetails from './pages/IntertownTripDetails';
import TrackRide from './pages/TrackRide';
import Profile from './pages/Profile';
import Intertown from './pages/Intertown';
import LegalDocumentScreen from './pages/LegalDocumentScreen';
import ErrorBoundary from './components/ErrorBoundary';
import OfflineBanner from './components/OfflineBanner';
import BottomNav from './components/BottomNav';
import RequiredUpdateGate from './components/RequiredUpdateGate';
import { playStoreUrl } from './utils/appLinks';

// On the live site /app and /download are served by public/app.html
// (firebase.json rewrites), which does the same with its own script; this
// only runs where those rewrites don't apply (local dev, inside the app).
const SmartAppRedirect = () => {
  useEffect(() => {
    const ua = navigator.userAgent || navigator.vendor || window.opera || '';
    const isAndroid = /android/i.test(ua);
    if (isAndroid) {
      window.location.replace(playStoreUrl(window.location.search));
    } else {
      window.location.replace('/');
    }
  }, []);
  return null;
};

const BlockedScreen = () => {
  const { t } = useLanguage();
  return (
    <div
      className="app"
      style={{
        padding: 'calc(24px + var(--safe-top)) 24px calc(24px + var(--safe-bottom))',
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 40, marginBottom: 12 }}>🚫</div>
      <h2>{t('app.blockedTitle')}</h2>
      <p className="text-muted">{t('app.blockedBody')}</p>
      <button type="button" className="btn btn-primary" style={{ marginTop: 20 }} onClick={() => logout()}>
        {t('profile.logout')}
      </button>
    </div>
  );
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
  }, [pathname]);
  return null;
};

const AppRoutes = () => {
  const {
    user, userProfile, authLoading, profileReady, profileLoaded, needsName,
    releaseNotice, activeRide, activeIntertownRequest, bookingFlowActive,
  } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const onLogin = location.pathname === '/login';
  const onTrack = location.pathname.startsWith('/track/');
  const onBook = location.pathname === '/';
  const onIntertown = location.pathname === '/intertown';
  const onPrimaryTab = onBook || onIntertown || location.pathname === '/rides' || location.pathname === '/profile';
  const bookVisited = React.useRef(false);
  // One persistent BottomNav lives here (not per-page) so its glider pill slides
  // between every tab. Book asks us to hide it while its booking sheet is open.
  const [bookChromeHidden, setBookChromeHidden] = React.useState(false);

  if (onBook) bookVisited.current = true;

  useEffect(() => {
    initAndroidBackBridge();
    checkForAppUpdate();
  }, []);

  // Route-level Android back (Book registers a higher-priority step handler).
  const hasActiveRide = Boolean(activeRide);
  useEffect(() => {
    return registerAndroidBackHandler(() => {
      const path = location.pathname;
      if (path.startsWith('/active/')) {
        // On a live trip there is nowhere to go "back" to: the ride list would
        // bounce straight back here (Book redirects active-ride users to
        // /active), remounting this screen and reloading the map. Let
        // MainActivity background the app instead, exactly like the home screen.
        if (hasActiveRide) return false;
        navigate('/rides', { replace: true });
        return true;
      }
      if (path.startsWith('/rides/')) {
        navigate('/rides', { replace: true });
        return true;
      }
      // Legal documents can be opened from Profile or Login. Going back in
      // browser history preserves whichever screen the user came from.
      if (path === '/privacy' || path === '/terms' || path === '/safety' || path.startsWith('/legal/')) {
        navigate(-1);
        return true;
      }
      if (path === '/rides' || path === '/profile' || path === '/intertown') {
        navigate('/', { replace: true });
        return true;
      }
      // Login / home / track: let MainActivity background the app.
      return false;
    });
  }, [location.pathname, navigate, hasActiveRide]);

  useEffect(() => {
    if (user?.uid) {
      initPassengerMessaging(user.uid, { request: false }).catch(() => {});
    }
    const unbindPush = handlePushDeepLink(navigate);
    const unbindGeo = initGeoDeepLinking(navigate);
    return () => {
      unbindPush?.();
      unbindGeo?.();
    };
  }, [user?.uid, navigate]);

  // Wait for auth + real Firestore profile before opening Book (avoids name flash + geo toasts).
  if (!onLogin && !onTrack && (authLoading || (user && (!profileReady || !profileLoaded)))) {
    return <Splash />;
  }

  if (!onTrack && user && userProfile?.isBlocked) return <BlockedScreen />;

  const gate = (page) => {
    if (!user) return <Navigate to="/login" replace />;
    if (needsName) return <Navigate to="/login" replace />;
    return page;
  };

  // Keep the expensive local-map tab alive across every primary tab once the
  // passenger has actually visited Book (bookVisited.current) — including
  // Intertown. Google bills the Maps JS API per map *load*, not per minute
  // rendered, so destroying and recreating it on every Book<->Intertown trip
  // was re-billing the same load each time; keeping the instance warm makes
  // the return trip free (just a resize) instead of a fresh, billed load.
  const keepBookMounted = Boolean(user && !needsName && (onBook || location.pathname === '/rides' || location.pathname === '/profile' || location.pathname === '/intertown') && bookVisited.current);
  // A live Intertown trip is a full-screen map with its own sheet (like a local ride on
  // /active), so the tab bar stays out of its way. Back still goes Home, where the bar is.
  const showBottomNav = Boolean(
    user && !needsName && onPrimaryTab && !(onBook && bookChromeHidden)
      && !(onIntertown && activeIntertownRequest),
  );

  return (
    <>
      {keepBookMounted && (
        <section
          className={`persistent-book-tab${onBook ? ' is-active' : ' is-inactive'}`}
          aria-hidden={!onBook}
        >
          <Book isTabActive={onBook} onChromeHiddenChange={setBookChromeHidden} />
        </section>
      )}

      <Routes>
        <Route path="/track/:token" element={<TrackRide />} />
        <Route
          path="/login"
          element={
              !user
              ? <Login />
              : !profileLoaded
                ? <Splash />
                : needsName
                  ? <Login startAt="name" />
                  : <Navigate to="/" replace />
          }
        />
        <Route path="/app" element={<SmartAppRedirect />} />
        <Route path="/download" element={<SmartAppRedirect />} />
        <Route path="/legal/:type" element={<LegalDocumentScreen />} />
        <Route path="/privacy" element={<LegalDocumentScreen type="privacy" />} />
        <Route path="/terms" element={<LegalDocumentScreen type="terms" />} />
        <Route path="/safety" element={<LegalDocumentScreen type="safety" />} />
        <Route path="/" element={gate(null)} />
        <Route path="/active/:rideId" element={gate(<ActiveRide />)} />
        <Route path="/rides" element={gate(<RideHistory />)} />
        <Route path="/rides/:rideId" element={gate(<RideDetails />)} />
        <Route path="/rides/intertown/:requestId" element={gate(<IntertownTripDetails />)} />
        <Route path="/intertown" element={gate(<Intertown />)} />
        <Route path="/profile" element={gate(<Profile />)} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {showBottomNav && <BottomNav />}
      {user && (
        <RequiredUpdateGate
          notice={releaseNotice}
          activeRide={activeRide}
          bookingFlowActive={bookingFlowActive}
        />
      )}
    </>
  );
};

export default function App() {
  const SkipLink = () => {
    const { t } = useLanguage();
    return <a href="#main-content" className="skip-link">{t('app.skipToContent')}</a>;
  };

  return (
    <ThemeProvider>
      <LanguageProvider>
        <AppProvider>
          <BrowserRouter>
            <ScrollToTop />
            <SkipLink />
            <OfflineBanner />
            <main id="main-content">
              <ErrorBoundary>
                <AppRoutes />
              </ErrorBoundary>
            </main>
            <Toaster
              position="top-center"
              containerStyle={{ top: 'calc(8px + var(--safe-top))' }}
              toastOptions={{
                style: {
                  fontFamily: 'DM Sans, Noto Sans Telugu, sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  borderRadius: '14px',
                  background: '#ffffff',
                  color: '#0d1117',
                  border: '1px solid #dce5e0',
                  boxShadow: '0 4px 16px rgba(13,17,23,0.08)',
                },
              }}
            />
          </BrowserRouter>
        </AppProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}
